import React, { useEffect, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { LeDataImageAttachment } from '~/types/leData';

import { cropRegionImage } from './wandCrop';
import {
  type WandElement,
  type WandFrame,
  type WandPoint,
  axisAlignedRectangle,
  closeRing,
  resolveWandSelection,
  ringBounds,
  strokeUsable,
  wandContextText,
  wandDrawStatus,
  wandQuestion,
} from './wandGeometry';
import { harvestWandElements, readCardFrames } from './wandHarvest';

const GLYPH_CAP = 48;
const SVG_NS = 'http://www.w3.org/2000/svg';
const RECT_HINT = 'Drag a rectangle, then release';

export type WandTurn = {
  question: string;
  image?: LeDataImageAttachment;
  context: { kind: 'lens_wand'; text: string; applies: 'this_turn' };
};

function paintDom(root: HTMLElement, active: Set<HTMLElement>, color: string) {
  root.querySelectorAll<HTMLElement>('[data-lens-wand]').forEach((node) => {
    const on = active.has(node);
    node.style.outline = on ? `1.5px solid ${color}` : '';
    node.style.outlineOffset = on ? '2px' : '';
  });
}

function overlayPoint(overlay: HTMLElement, event: React.PointerEvent | PointerEvent): WandPoint {
  const box = overlay.getBoundingClientRect();
  return { x: event.clientX - box.left, y: event.clientY - box.top };
}

function toViewport(overlay: HTMLElement, points: ReadonlyArray<WandPoint>): Array<WandPoint> {
  const box = overlay.getBoundingClientRect();
  return points.map((point) => ({ x: point.x + box.left, y: point.y + box.top }));
}

function paintPath(node: SVGPathElement | null, points: ReadonlyArray<WandPoint>) {
  if (!node) {
    return;
  }
  if (points.length === 0) {
    node.setAttribute('visibility', 'hidden');
    node.setAttribute('d', '');
    return;
  }
  node.setAttribute(
    'd',
    `${points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')} Z`,
  );
  node.setAttribute('visibility', 'visible');
}

function paintGlyph(node: SVGElement, element: WandElement, originLeft: number, originTop: number, color: string) {
  if (element.hit.shape === 'box') {
    node.setAttribute('x', String(element.hit.box.left - originLeft));
    node.setAttribute('y', String(element.hit.box.top - originTop));
    node.setAttribute('width', String(element.hit.box.right - element.hit.box.left));
    node.setAttribute('height', String(element.hit.box.bottom - element.hit.box.top));
    node.setAttribute('fill', alpha(color, 0.18));
    return;
  }
  node.setAttribute('cx', String(element.hit.x - originLeft));
  node.setAttribute('cy', String(element.hit.y - originTop));
  node.setAttribute('r', String(Math.min(element.hit.r, 8)));
  node.setAttribute('fill', alpha(color, 0.35));
}

function paintGlyphs(
  group: SVGGElement | null,
  elements: ReadonlyArray<WandElement>,
  overlay: HTMLElement | null,
  color: string,
) {
  if (!group) {
    return;
  }
  const marks = elements.filter((element) => element.kind === 'chart-mark').slice(0, GLYPH_CAP);
  while (group.childNodes.length > marks.length) {
    const last = group.lastChild;
    if (!last) {
      break;
    }
    group.removeChild(last);
  }
  if (marks.length === 0 || !overlay) {
    return;
  }
  const box = overlay.getBoundingClientRect();
  marks.forEach((element, index) => {
    const want = element.hit.shape === 'box' ? 'rect' : 'circle';
    let node = group.childNodes[index] as SVGElement | undefined;
    if (!node || node.localName !== want) {
      const created = document.createElementNS(SVG_NS, want);
      created.setAttribute('stroke', color);
      created.setAttribute('stroke-width', '1');
      if (node) {
        group.replaceChild(created, node);
      } else {
        group.appendChild(created);
      }
      node = created;
    }
    node.setAttribute('stroke', color);
    paintGlyph(node, element, box.left, box.top, color);
  });
}

export function useLensWandAsk({
  canvasRef,
  cards,
  enabled,
  onCommit,
}: {
  canvasRef: React.RefObject<HTMLElement | null>;
  cards: Array<{ id: string; title: string }>;
  enabled: boolean;
  onCommit: (turn: WandTurn) => void;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [armed, setArmed] = useState(false);
  const [hint, setHint] = useState(RECT_HINT);
  const [reading, setReading] = useState(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const glyphGroupRef = useRef<SVGGElement | null>(null);
  const statusNodeRef = useRef<HTMLParagraphElement | null>(null);
  const strokeRef = useRef<Array<WandPoint>>([]);
  const anchorRef = useRef<WandPoint | null>(null);
  const candidatesRef = useRef<Array<WandElement>>([]);
  const framesRef = useRef<Array<WandFrame>>([]);
  const previewFrameRef = useRef<number | null>(null);
  const previewStatusRef = useRef<string | null>(null);
  const committingRef = useRef(false);
  const generationRef = useRef(0);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const accentRef = useRef(tokens.accent);
  accentRef.current = tokens.accent;
  const titlesRef = useRef(new Map(cards.map((card) => [card.id, card.title])));
  titlesRef.current = new Map(cards.map((card) => [card.id, card.title]));

  const remember = (next: Array<WandPoint>) => {
    strokeRef.current = next;
    paintPath(pathRef.current, next);
  };

  const applyStatus = (text: string) => {
    previewStatusRef.current = text;
    const node = statusNodeRef.current;
    if (!node) {
      return;
    }
    if (node.textContent !== text) {
      node.textContent = text;
    }
    if (node.getAttribute('data-lens-wand-status') !== text) {
      node.setAttribute('data-lens-wand-status', text);
    }
  };

  const clearPaint = () => {
    const root = canvasRef.current;
    if (root) {
      paintDom(root, new Set(), accentRef.current);
    }
    paintGlyphs(glyphGroupRef.current, [], overlayRef.current, accentRef.current);
    previewStatusRef.current = null;
  };

  const cancelPreview = () => {
    if (previewFrameRef.current == null) {
      return;
    }
    window.cancelAnimationFrame(previewFrameRef.current);
    previewFrameRef.current = null;
  };

  const preview = (points: Array<WandPoint>) => {
    const root = canvasRef.current;
    const overlay = overlayRef.current;
    const box = points.length >= 4 ? ringBounds(points) : null;
    const width = box ? box.right - box.left : 0;
    const height = box ? box.bottom - box.top : 0;
    if (!root || !overlay || width < 1 || height < 1) {
      if (root) {
        paintDom(root, new Set(), accentRef.current);
      }
      paintGlyphs(glyphGroupRef.current, [], overlay, accentRef.current);
      if (points.length > 0) {
        applyStatus(RECT_HINT);
      }
      return null;
    }
    const ring = closeRing(toViewport(overlay, points));
    const selection = resolveWandSelection(framesRef.current, candidatesRef.current, ring);
    paintDom(
      root,
      new Set(selection.elements.flatMap((element) => (element.node ? [element.node] : []))),
      accentRef.current,
    );
    paintGlyphs(glyphGroupRef.current, selection.elements, overlay, accentRef.current);
    applyStatus(wandDrawStatus(selection, true));
    return selection;
  };

  const schedulePreview = () => {
    if (previewFrameRef.current != null) {
      return;
    }
    previewFrameRef.current = window.requestAnimationFrame(() => {
      previewFrameRef.current = null;
      preview(strokeRef.current);
    });
  };

  useEffect(() => {
    return () => {
      generationRef.current += 1;
      if (previewFrameRef.current != null) {
        window.cancelAnimationFrame(previewFrameRef.current);
        previewFrameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setArmed(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (armed) {
      setHint(RECT_HINT);
      return;
    }
    cancelPreview();
    generationRef.current += 1;
    committingRef.current = false;
    setReading(false);
    anchorRef.current = null;
    strokeRef.current = [];
    paintPath(pathRef.current, []);
    candidatesRef.current = [];
    framesRef.current = [];
    previewStatusRef.current = null;
    paintGlyphs(glyphGroupRef.current, [], null, tokens.accent);
    const root = canvasRef.current;
    if (root) {
      paintDom(root, new Set(), tokens.accent);
    }
  }, [armed, canvasRef, tokens.accent]);

  useEffect(() => {
    if (!armed) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setArmed(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [armed]);

  const finish = async () => {
    anchorRef.current = null;
    if (committingRef.current) {
      return;
    }
    cancelPreview();
    const overlay = overlayRef.current;
    const root = canvasRef.current;
    const points = strokeRef.current;
    if (!overlay || !root || !strokeUsable(points)) {
      remember([]);
      clearPaint();
      setHint('Drag a rectangle around the marks you want to ask about');
      return;
    }
    const ring = closeRing(toViewport(overlay, points));
    const selection = resolveWandSelection(readCardFrames(root, titlesRef.current), candidatesRef.current, ring);
    framesRef.current = [];
    if (selection.cards.length === 0 && selection.elements.length === 0) {
      remember([]);
      clearPaint();
      setHint('The wand did not catch a card or a mark');
      return;
    }
    committingRef.current = true;
    setReading(true);
    setHint('Reading this region…');
    const ticket = generationRef.current;
    paintDom(root, new Set(), accentRef.current);
    const image = await cropRegionImage(root, ring);
    if (ticket !== generationRef.current) {
      return;
    }
    const question = wandQuestion();
    const text = wandContextText({ ...selection, image: Boolean(image) });
    remember([]);
    clearPaint();
    setReading(false);
    setArmed(false);
    commitRef.current({
      question,
      image: image ?? undefined,
      context: { kind: 'lens_wand', text, applies: 'this_turn' },
    });
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || committingRef.current) {
      return;
    }
    const overlay = overlayRef.current;
    const root = canvasRef.current;
    if (!overlay || !root) {
      return;
    }
    cancelPreview();
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    candidatesRef.current = harvestWandElements(root, titlesRef.current);
    framesRef.current = readCardFrames(root, titlesRef.current);
    const origin = overlayPoint(overlay, event);
    anchorRef.current = origin;
    setHint(RECT_HINT);
    remember(axisAlignedRectangle(origin, origin));
    preview(strokeRef.current);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const overlay = overlayRef.current;
    const anchor = anchorRef.current;
    if (!overlay || !anchor || committingRef.current) {
      return;
    }
    const next = axisAlignedRectangle(anchor, overlayPoint(overlay, event));
    const current = strokeRef.current;
    if (
      current.length === next.length &&
      current.every((point, index) => point.x === next[index].x && point.y === next[index].y)
    ) {
      return;
    }
    remember(next);
    schedulePreview();
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    void finish();
  };

  const status = reading ? 'Reading this region…' : (previewStatusRef.current ?? hint);

  const button = enabled ? (
    <Stack alignItems="center" direction="row" spacing={1}>
      {armed ? (
        <Typography
          aria-live="polite"
          data-lens-wand-status={status}
          ref={statusNodeRef}
          sx={{ color: 'text.secondary', fontSize: 12.5, whiteSpace: 'nowrap' }}
        >
          {status}
        </Typography>
      ) : null}
      <Tooltip title={armed ? 'Put the wand away' : 'Magic wand'}>
        <IconButton
          aria-label="Magic wand"
          aria-pressed={armed}
          data-lens-wand-ask={armed ? 'armed' : 'idle'}
          onClick={() => setArmed((current) => !current)}
          size="small"
          sx={{
            backgroundColor: armed ? alpha(tokens.accent, 0.12) : 'transparent',
            border: '1px solid',
            borderColor: armed ? tokens.accent : 'divider',
            color: armed ? tokens.accent : 'text.secondary',
          }}
        >
          <AutoFixHighIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : null;

  const overlay =
    enabled && armed ? (
      <Box
        data-lens-wand-overlay=""
        onPointerCancel={() => {
          anchorRef.current = null;
          cancelPreview();
          framesRef.current = [];
          remember([]);
          clearPaint();
          applyStatus(RECT_HINT);
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        ref={overlayRef}
        sx={{
          cursor: 'crosshair',
          inset: 0,
          position: 'absolute',
          touchAction: 'none',
          userSelect: 'none',
          zIndex: 8,
        }}
      >
        <Box
          aria-hidden
          component="svg"
          height="100%"
          sx={{ inset: 0, pointerEvents: 'none', position: 'absolute' }}
          width="100%"
        >
          <path
            fill={alpha(tokens.accent, 0.08)}
            ref={pathRef}
            stroke={tokens.accent}
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
          <g ref={glyphGroupRef} />
        </Box>
      </Box>
    ) : null;

  return { button, overlay };
}
